import React, { useState, useEffect, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import classnames from "classnames";
import api from "../../api";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { useLocation, useNavigate } from "react-router-dom";
import jwt_decode from "jwt-decode";

// Import the separate tab components
import PlayerProfileBoxTab from "../../components/Profile/PlayerProfileBoxTab";
import PlayerProfileGiftTab from "../../components/Profile/PlayerProfileGiftTab";
import PlayerProfileMainTab from "../../components/Profile/PlayerProfileMainTab";
import PlayerProfileInventoryTab from "../../components/Profile/PlayerProfileInventoryTab";
import SendMessageProfile from "../Messages/SendMessageProfile";
import PlayerProfileTradeTab from "../../components/Trades/PlayerProfileTradeTab";
import UsernameSearchSuggestions from "../../components/Profile/UsernameSearchSuggestions";
import "./PlayerProfileError.css";

const PlayerProfile = () => {
  document.title = "Pokemon Radiance | Player Profile Search";
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const initialUser = params.get("user") || "";

  // Active tab: "main", "inventory", "box", "message", "trade", "gift"
  const [activeTab, setActiveTab] = useState("main");

  // Profile-related states
  const [searchQuery, setSearchQuery] = useState(initialUser);
  const [profile, setProfile] = useState(null);
  const [catcherLevel, setCatcherLevel] = useState(null);
  const [roster, setRoster] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [box, setBox] = useState([]); // (Loaded in PlayerProfileBoxTab)
  const [error, setError] = useState("");
  const [usernameSuggestions, setUsernameSuggestions] = useState([]);

  // Filter states for inventory and box
  const [inventorySearch, setInventorySearch] = useState("");

  // -------------- Send Gift Tab States (Managed in Parent) --------------
  // For gift-eligible Pokémon (paginated)
  const rowsPerPage = 10;
  const [giftPokemonList, setGiftPokemonList] = useState([]);
  const [giftLoading, setGiftLoading] = useState(false);
  const [giftTotalPages, setGiftTotalPages] = useState(0);
  const [giftPage, setGiftPage] = useState(1);
  const [giftPokemonType, setGiftPokemonType] = useState("all");
  const [giftPokemonSearch, setGiftPokemonSearch] = useState("");
  const [selectedGiftPokemon, setSelectedGiftPokemon] = useState(null);
  const [rankings, setRankings] = useState(null);

  // For gift items and currency
  const [currency, setCurrency] = useState(null);
  const [giftItemList, setGiftItemList] = useState([]);
  const [giftItemSearch, setGiftItemSearch] = useState("");
  const [selectedGiftItem, setSelectedGiftItem] = useState(null);
  const [giftItemAmount, setGiftItemAmount] = useState("");

  // For currency sending
  const [coinsToSend, setCoinsToSend] = useState("");
  const [moneyToSend, setMoneyToSend] = useState("");

  // Accordion state for Send Gift tab
  const [openAccordion, setOpenAccordion] = useState(["1", "2", "3"]);
  const toggleAccordion = (id) => {
    if (openAccordion.includes(id)) {
      setOpenAccordion(openAccordion.filter((item) => item !== id));
    } else {
      setOpenAccordion([...openAccordion, id]);
    }
  };

  // Popup modal state
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [popupImage, setPopupImage] = useState("");
  const [popupColor, setPopupColor] = useState("");

  // Get username from JWT token with fallback to authUser in localStorage
  const getUsername = () => {
    const token = localStorage.getItem("jwtToken");
    if (token) {
      try {
        const decoded = jwt_decode(token);
        return decoded.sub || decoded.username;
      } catch (error) {
        console.error("Error decoding token:", error);
      }
    }
    return localStorage.getItem("authUser"); // Fallback to authUser if token is invalid
  };
  
  const currentUser = getUsername();

  // Helper to show a popup for 6 seconds
  const showPopupMessage = (message, image, color) => {
    setPopupMessage(message);
    setPopupImage(image);
    setPopupColor(color);
    setShowPopup(true);
    setTimeout(() => setShowPopup(false), 6000);
  };

  // Fetch all profile data except for leaderboard and gift Pokémon.
  // Now accepts `username` directly, instead of reading from state.
  const fetchAllProfileData = async (username) => {
    const queryParam = `?username=${encodeURIComponent(username)}`;
    try {
      // Fetch profile, roster, rankings in parallel
      const [profileRes, rosterRes, rankingsRes] = await Promise.all([
        api.get(`/v1/profile/search${queryParam}`, { withCredentials: true }),
        api.get(`/v1/roster/search${queryParam}`, { withCredentials: true }),
        api.get(`/v1/leaderboard/rankings${queryParam}`, {
          withCredentials: true,
        }),
      ]);
      setCatcherLevel(profileRes.data.catcher_exp);
      
      // Profile data now includes last_active and is_online from the backend
      const profileData = profileRes.data;
      
      // Format the last active time for display if needed
      setProfile(profileData);
      setRoster(rosterRes.data);
      setRankings(rankingsRes.data);

      // Fetch inventory, box in parallel
      const [inventoryRes, boxRes] = await Promise.all([
        api.get(`/v1/inventory/search${queryParam}`, { withCredentials: true }),
        api.get(`/v1/pokemon`, { withCredentials: true }),
      ]);
      setInventory(inventoryRes.data);
      setBox(boxRes.data.data);

      // Fetch giftable pokemon, items, currency in parallel
      const [giftPokemonRes, giftItemsRes, currencyRes] = await Promise.all([
        api.get(`/v1/pokemon`, { withCredentials: true }),
        api.get(`/v1/inventory`, { withCredentials: true }),
        api.get(`/v1/currency`, { withCredentials: true }),
      ]);

      // Filter eligible pokemon: slot === 0 and (no nft field or nft===0)
      const eligiblePokemon = giftPokemonRes.data.data.filter(
        (poke) => poke.slot === 0 && (poke.nft === undefined || poke.nft === 0)
      );
      setGiftPokemonList(eligiblePokemon);
      setGiftItemList(giftItemsRes.data);
      setCurrency(currencyRes.data[0]);
      setError(null);
    } catch (err) {
      console.error("Failed to fetch full profile data:", err);
      setError("Failed to load profile data. Please try again.");
      setProfile(null);
      setRoster([]);
      setInventory([]);
      setBox([]);
      setRankings(null);
      setGiftPokemonList([]);
      setGiftItemList([]);
      setCurrency(null);
      
      // Fetch username suggestions when profile search fails
      try {
        const suggestionsRes = await api.get(`/v1/profile/suggest?username=${username}`, { withCredentials: true });
        setUsernameSuggestions(suggestionsRes.data);
      } catch (suggestErr) {
        console.error("Failed to fetch username suggestions:", suggestErr);
        setUsernameSuggestions([]);
      }
    }
  };

  // Fetch gift-eligible Pokémon (paginated) for gifting.
  const fetchGiftPokemon = (page = 1) => {
    setGiftLoading(true);
    api
      .get("/v1/pokemon", {
        params: {
          page,
          limit: rowsPerPage,
          type: giftPokemonType !== "all" ? giftPokemonType : undefined,
        },
        withCredentials: true,
      })
      .then((res) => {
        // Only allow Pokémon with slot <= 0 to be gifted.
        const filtered = res.data.data.filter((pokemon) => pokemon.slot <= 0);
        setGiftPokemonList(filtered);
        setGiftTotalPages(Math.ceil(res.data.total / rowsPerPage));
        setGiftPage(res.data.page);
        setGiftLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching gift Pokémon:", err);
        setGiftLoading(false);
      });
  };

  // Search for gift-eligible Pokémon.
  const searchGiftPokemon = (query) => {
    if (!query) return fetchGiftPokemon(giftPage);
    setGiftLoading(true);
    api
      .get("/v1/pokemon/search", {
        params: {
          query,
          type: giftPokemonType !== "all" ? giftPokemonType : undefined,
        },
        withCredentials: true,
      })
      .then((res) => {
        // Assume the search returns an array of matching Pokémon.
        setGiftPokemonList(res.data);
        setGiftTotalPages(1);
        setGiftPage(1);
        setGiftLoading(false);
      })
      .catch((err) => {
        console.error("Error searching gift Pokémon:", err);
        setGiftLoading(false);
      });
  };

  // Local filtering on the fetched gift Pokémon list (by search text).
  const filteredGiftPokemon = useMemo(() => {
    return giftPokemonList.filter((poke) =>
      poke.name.toLowerCase().includes(giftPokemonSearch.toLowerCase())
    );
  }, [giftPokemonList, giftPokemonSearch]);

  // Handler to send a Pokémon gift.
  const handleSendPokemonGift = async () => {
    if (!selectedGiftPokemon || !profile) return;
    try {
      const payload = {
        target_user_id: profile.id,
        gift_type: "pokemon",
        gift_id: selectedGiftPokemon.id,
      };
      await api.post("/v1/send-gift", payload, { withCredentials: true });
      showPopupMessage(
        "Pokemon gift sent successfully.",
        `/sprites/${selectedGiftPokemon.name}.png`,
        "#4CAF50"
      );
      setSelectedGiftPokemon(null);
      // Re-fetch the current page of gift Pokémon.
      fetchGiftPokemon(giftPage);
    } catch (err) {
      console.error(err);
      showPopupMessage("Failed to send pokemon gift.", "", "#f44336");
    }
  };

  // (Other functions for sending item and currency gifts remain unchanged.)
  const handleSendItemGift = async () => {
    if (!selectedGiftItem || !giftItemAmount) return;
    try {
      const payload = {
        target_user_id: profile.id,
        gift_type: "item",
        gift_id: selectedGiftItem.item,
        amount: Number(giftItemAmount),
      };
      await api.post("/v1/send-gift", payload, { withCredentials: true });
      showPopupMessage(
        "Item gift sent successfully.",
        `${selectedGiftItem.item_image}`,
        "#4CAF50"
      );
      setSelectedGiftItem(null);
      setGiftItemAmount("");
      // Optionally re-fetch gift items if needed
    } catch (err) {
      console.error(err);
      showPopupMessage("Failed to send item gift.", "", "#f44336");
    }
  };

  const handleSendCurrencyGift = async (type) => {
    const value = type === "coins" ? coinsToSend : moneyToSend;
    if (!value) return;
    try {
      const payload = {
        target_user_id: profile.id,
        gift_type: type,
        amount: Number(value),
      };
      await api.post("/v1/send-gift", payload, { withCredentials: true });
      const icon = type === "coins" ? "/items/coin.png" : "/items/money.png";
      showPopupMessage(
        `Successfully sent ${payload.amount.toLocaleString()} ${type}`,
        icon,
        "#4CAF50"
      );
      if (type === "coins") setCoinsToSend("");
      else setMoneyToSend("");
      // Optionally re-fetch currency if needed
    } catch (err) {
      console.error(err);
      showPopupMessage(`Failed to send ${type} gift.`, "", "#f44336");
    }
  };

  // Tab switching handler
  const toggleTab = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  // Utility function for level calculation.
  function getLevel(exp) {
    const lvl = Math.floor(Math.pow(exp, 1 / 3));
    return Math.pow(lvl + 1, 3) <= exp ? lvl + 1 : lvl;
  }
  const level = profile ? getLevel(profile.exp) : 0;

  // On mount / whenever the URL’s “?user=” changes, fetch data for that username.
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const userParam = searchParams.get("user") || "";

    if (
      currentUser &&
      (userParam === "" ||
        userParam.toLowerCase() === currentUser.toLowerCase())
    ) {
      navigate("/profile");
    } else if (userParam) {
      // Immediately set state so the URL and state match
      setSearchQuery(userParam);
      setActiveTab("main");
      fetchAllProfileData(userParam);
    } else {
      // If there’s no ?user=, clear out everything
      setProfile(null);
      setRoster([]);
      setInventory([]);
      setBox([]);
      setGiftPokemonList([]);
      setGiftItemList([]);
      setCurrency(null);
      setError("");
    }
    // Clear suggestions when URL changes
    setUsernameSuggestions([]);
  }, [location.search, currentUser, navigate]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs
            title="Profile Search"
            breadcrumbItem="Player Profile"
          />
          {error && (
            <div className="profile-error-container">
              <p className="profile-error-message">{error}</p>
              {usernameSuggestions.length > 0 && (
                <UsernameSearchSuggestions 
                  suggestions={usernameSuggestions} 
                  onSuggestionClick={(username) => {
                    setSearchQuery(username);
                    setUsernameSuggestions([]);
                    // Navigate to the selected profile
                    navigate(`/profile?user=${encodeURIComponent(username)}`);
                  }} 
                />
              )}
            </div>
          )}
          {profile && (
            <Row>
              <Col md={12}>
                <Nav tabs>
                  <NavItem>
                    <NavLink
                      className={classnames({ active: activeTab === "main" })}
                      onClick={() => toggleTab("main")}
                      style={{ cursor: "pointer" }}
                    >
                      Main
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={classnames({
                        active: activeTab === "inventory",
                      })}
                      onClick={() => toggleTab("inventory")}
                      style={{ cursor: "pointer" }}
                    >
                      Inventory
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={classnames({ active: activeTab === "box" })}
                      onClick={() => toggleTab("box")}
                      style={{ cursor: "pointer" }}
                    >
                      Box
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={classnames({
                        active: activeTab === "message",
                      })}
                      onClick={() => toggleTab("message")}
                      style={{ cursor: "pointer" }}
                    >
                      Send Message
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={classnames({
                        active: activeTab === "trade",
                      })}
                      onClick={() => toggleTab("trade")}
                      style={{ cursor: "pointer" }}
                    >
                      Create Trade
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={classnames({ active: activeTab === "gift" })}
                      onClick={() => toggleTab("gift")}
                      style={{ cursor: "pointer" }}
                    >
                      Send Gift
                    </NavLink>
                  </NavItem>
                </Nav>
                <TabContent activeTab={activeTab} className="p-3">
                  <TabPane tabId="main">
                    <PlayerProfileMainTab
                      profile={profile}
                      roster={roster}
                      level={level}
                      getLevel={getLevel}
                      catcherLevel={catcherLevel}
                      rankings={rankings}
                    />
                  </TabPane>
                  <TabPane tabId="inventory">
                    <PlayerProfileInventoryTab
                      inventory={inventory}
                      inventorySearch={inventorySearch}
                      setInventorySearch={setInventorySearch}
                      filteredInventory={inventory.filter((item) =>
                        item.item_name
                          .toLowerCase()
                          .includes(inventorySearch.toLowerCase())
                      )}
                    />
                  </TabPane>
                  <TabPane tabId="box">
                    {activeTab === "box" && <PlayerProfileBoxTab username={profile?.username} />}
                  </TabPane>
                  <TabPane tabId="message">
                    <SendMessageProfile />
                  </TabPane>
                  <TabPane tabId="trade">
                    <PlayerProfileTradeTab
                      profile={profile}
                      box={box}
                      inventory={inventory}
                      giftItemList={giftItemList}
                      currency={currency}
                      showPopupMessage={showPopupMessage}
                    />
                  </TabPane>
                  <TabPane tabId="gift">
                    <PlayerProfileGiftTab
                      // Gift Pokémon props
                      giftSearch={giftPokemonSearch}
                      setGiftSearch={setGiftPokemonSearch}
                      giftPokemonType={giftPokemonType}
                      setGiftPokemonType={setGiftPokemonType}
                      filteredGiftPokemon={filteredGiftPokemon}
                      selectedGiftPokemon={selectedGiftPokemon}
                      setSelectedGiftPokemon={setSelectedGiftPokemon}
                      handleSendPokemonGift={handleSendPokemonGift}

                      // Box is still needed elsewhere, so we pass it along
                      box={box}

                      // Gift Item props
                      giftItemSearch={giftItemSearch}
                      setGiftItemSearch={setGiftItemSearch}
                      giftItemAmount={giftItemAmount}
                      setGiftItemAmount={setGiftItemAmount}
                      filteredGiftItems={giftItemList.filter((item) => {
                        const pickaxeItems = [
                          'Normal Pickaxe',
                          'Ruby Pickaxe', 
                          'Emerald Pickaxe',
                          'Sapphire Pickaxe',
                          'Golden Pickaxe',
                          'Diamond Pickaxe'
                        ];
                        
                        const fishingRods = [
                          'Old Rod',
                          'Good Rod',
                          'Super Rod'
                        ];
                        
                        const untradableItems = [...pickaxeItems, ...fishingRods];
                        
                        return !untradableItems.includes(item.item_name) &&
                               item.item_name
                                 .toLowerCase()
                                 .includes(giftItemSearch.toLowerCase());
                      })}
                      selectedGiftItem={selectedGiftItem}
                      setSelectedGiftItem={setSelectedGiftItem}
                      handleSendItemGift={handleSendItemGift}

                      // Currency props
                      coinsToSend={coinsToSend}
                      setCoinsToSend={setCoinsToSend}
                      moneyToSend={moneyToSend}
                      setMoneyToSend={setMoneyToSend}
                      handleSendCurrencyGift={handleSendCurrencyGift}

                      // Pagination & loading for gift Pokémon
                      openAccordion={openAccordion}
                      toggleAccordion={toggleAccordion}
                      getLevel={getLevel}
                      myLoading={giftLoading}
                      myPage={giftPage}
                      myTotalPages={giftTotalPages}
                      fetchMyPokemon={fetchGiftPokemon}
                      searchMyPokemon={searchGiftPokemon}
                      showPopupMessage={showPopupMessage}
                      profile={profile}
                    />
                  </TabPane>
                </TabContent>
              </Col>
            </Row>
          )}
        </Container>
      </div>

      {/* Popup Modal */}
      {showPopup && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.75)",
            backdropFilter: "blur(5px)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              backgroundColor: "rgba(25, 25, 35, 0.95)",
              borderRadius: "12px",
              width: "90%",
              maxWidth: "400px",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1)",
              overflow: "hidden",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              animation: "fadeIn 0.3s ease-out",
            }}
          >
            {/* Header */}
            <div
              style={{
                padding: "16px 20px",
                borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background: "rgba(0, 0, 0, 0.2)",
              }}
            >
              <h5 style={{ margin: 0, color: "#fff", fontWeight: "600" }}>
                {popupMessage.includes("successfully") ? "Success" : "Notification"}
              </h5>
              <button
                onClick={() => setShowPopup(false)}
                style={{
                  background: "none",
                  border: "none",
                  color: "#fff",
                  fontSize: "20px",
                  cursor: "pointer",
                  padding: "8px 12px",
                  borderRadius: "4px",
                  opacity: 0.7,
                  flexShrink: 0,
                  minWidth: "40px",
                  height: "40px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onMouseEnter={(e) => (e.target.style.opacity = "1")}
                onMouseLeave={(e) => (e.target.style.opacity = "0.7")}
              >
                ✕
              </button>
            </div>
            
            {/* Body */}
            <div
              style={{
                padding: "30px 20px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
              }}
            >
              {popupImage && (
                <div
                  style={{
                    width: "100px",
                    height: "100px",
                    margin: "0 auto 20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "rgba(0, 0, 0, 0.2)",
                    borderRadius: "10px",
                    padding: "15px",
                    boxShadow: "0 4px 15px rgba(0, 0, 0, 0.3)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                  }}
                >
                  <img
                    src={popupImage}
                    alt=""
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                      filter: "drop-shadow(0 2px 8px rgba(255, 255, 255, 0.3))",
                    }}
                  />
                </div>
              )}
              <div
                style={{
                  fontSize: "16px",
                  fontWeight: "bold",
                  color: popupColor || "#fff",
                  textShadow: "0 2px 4px rgba(0, 0, 0, 0.5)",
                  marginTop: popupImage ? "10px" : "0",
                }}
              >
                {popupMessage}
              </div>
            </div>
            
            {/* Footer */}
            <div
              style={{
                padding: "12px 20px",
                borderTop: "1px solid rgba(255, 255, 255, 0.1)",
                display: "flex",
                justifyContent: "center",
                background: "rgba(0, 0, 0, 0.2)",
              }}
            >
              <button
                onClick={() => setShowPopup(false)}
                style={{
                  background: "rgba(255, 255, 255, 0.1)",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  color: "#fff",
                  padding: "8px 16px",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = "rgba(255, 255, 255, 0.2)";
                  e.target.style.transform = "translateY(-1px)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = "rgba(255, 255, 255, 0.1)";
                  e.target.style.transform = "translateY(0)";
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </React.Fragment>
  );
};

export default PlayerProfile;
